import React, { useState, useEffect } from 'react';
import {
  Atom,
  CheckCircle,
  BookOpen,
  Target,
  Brain,
  PenTool,
  ArrowRight,
  Sparkles,
  GraduationCap,
  ClipboardCheck,
  TrendingUp,
  Zap,
  Shield,
  Clock,
  Award,
  ChevronRight,
  MessageSquare,
  BarChart3,
  Lightbulb,
  Calculator,
  Menu,
  X
} from 'lucide-react';

interface LandingPageProps {
  onSignIn: () => void;
  onSignUp: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onSignIn, onSignUp }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: Target,
      title: 'Topic-Focused Practice',
      description: 'Questions mapped to exact spec points for AQA, OCR, and Edexcel boards.',
      color: 'from-purple-500 to-cobalt-600'
    },
    {
      icon: ClipboardCheck,
      title: 'Examiner-Style Feedback',
      description: 'See where marks are awarded and lost, just like a real exam.',
      color: 'from-emerald-500 to-teal-600'
    },
    {
      icon: PenTool,
      title: 'Interactive Whiteboard',
      description: 'Draw diagrams, show working, and annotate your notes.',
      color: 'from-orange-500 to-amber-600'
    },
    {
      icon: Brain,
      title: 'Smart Active Recall',
      description: 'AI-powered spaced repetition to strengthen memory.',
      color: 'from-purple-500 to-violet-600'
    },
    {
      icon: TrendingUp,
      title: 'Progress Tracking',
      description: 'Visual dashboards showing improvement across all topics.',
      color: 'from-rose-500 to-pink-600'
    },
    {
      icon: Lightbulb,
      title: 'Instant Explanations',
      description: 'Struggling? Get step-by-step guidance when you need it.',
      color: 'from-cyan-500 to-purple-600'
    }
  ];

  const subjects = [
    'Physics', 'Chemistry', 'Biology', 'Maths', 'Further Maths',
    'Economics', 'English Literature', 'History', 'Geography'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-900 overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/90 backdrop-blur-xl shadow-sm border-b border-slate-200/50' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img src="/absorbit-logo.png" alt="Absorbit" className="h-28 md:h-36 w-auto" />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-2">
              <a href="#features" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
                Features
              </a>
              <a href="#pricing" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
                Pricing
              </a>
              <button onClick={onSignIn} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
                Sign in
              </button>
              <button
                onClick={onSignUp}
                className="ml-2 px-5 py-2.5 bg-cobalt-600 hover:bg-cobalt-500 text-white text-sm font-semibold rounded-full shadow-lg shadow-cobalt-500/25 hover:shadow-cobalt-500/40 transition-all flex items-center gap-2"
              >
                Get Started
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-slate-600 hover:text-slate-900"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 shadow-xl">
            <div className="px-4 py-4 space-y-3">
              <a href="#features" className="block px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg">Features</a>
              <a href="#pricing" className="block px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg">Pricing</a>
              <button onClick={onSignIn} className="block w-full text-left px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg">Sign in</button>
              <button
                onClick={onSignUp}
                className="w-full px-4 py-3 bg-cobalt-600 text-white font-semibold rounded-xl"
              >
                Get Started Free
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 md:pt-32 lg:pt-40 pb-16 md:pb-24 px-4 sm:px-6 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-1/4 w-72 h-72 bg-cobalt-200/30 rounded-full blur-3xl" />
          <div className="absolute top-40 right-1/4 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-1/2 bg-gradient-to-t from-slate-50 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto relative">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cobalt-50 border border-cobalt-100 rounded-full text-cobalt-700 text-sm font-medium mb-6 md:mb-8">
              <GraduationCap className="w-4 h-4" />
              <span>GCSE & A-Level Revision Platform</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight mb-6 md:mb-8">
              <span className="text-slate-900">Study smarter with</span>
              <br />
              <span className="text-cobalt-600">
                AI-powered feedback
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed">
              Get examiner-quality marking on every practice question. See exactly where you earn marks,
              where you lose them, and what to do next.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 md:mb-16">
              <button
                onClick={onSignUp}
                className="w-full sm:w-auto px-8 py-4 bg-cobalt-600 hover:bg-cobalt-500 text-white text-lg font-semibold rounded-2xl shadow-xl shadow-cobalt-500/25 hover:shadow-cobalt-500/40 transition-all flex items-center justify-center gap-2"
              >
                Start Revising Free
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>AQA, OCR & Edexcel</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>9 subjects covered</span>
              </div>
            </div>
          </div>

          {/* Hero Preview Card */}
          <div className="mt-12 md:mt-20 max-w-4xl mx-auto">
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-cobalt-500/20 via-purple-500/20 to-cobalt-500/20 rounded-3xl blur-2xl" />

              {/* Card */}
              <div className="relative bg-white rounded-2xl md:rounded-3xl shadow-2xl shadow-slate-200/50 border border-slate-200/50 overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-yellow-400" />
                      <div className="w-3 h-3 rounded-full bg-green-400" />
                    </div>
                    <span className="text-slate-400 text-xs md:text-sm font-medium">Atlas AI Feedback</span>
                  </div>
                  <div className="flex items-center gap-2 text-emerald-400 text-xs md:text-sm font-bold">
                    <Award className="w-4 h-4" />
                    <span>3/4 marks</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 md:p-8 space-y-4 md:space-y-6">
                  {/* Question Context */}
                  <div className="bg-slate-50 rounded-xl md:rounded-2xl p-4 md:p-5 border border-slate-100">
                    <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Physics • Forces</p>
                    <p className="text-slate-700 text-sm md:text-base">Calculate the resultant force on a 2kg object accelerating at 3 m/s². Show your working.</p>
                  </div>

                  {/* Feedback Cards */}
                  <div className="grid md:grid-cols-3 gap-3 md:gap-4">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl md:rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
                        <span className="text-emerald-800 font-semibold text-sm">Correct</span>
                      </div>
                      <p className="text-emerald-700 text-xs md:text-sm">Substitution into F = ma</p>
                      <p className="text-emerald-600 text-xs mt-1">+1 mark</p>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-xl md:rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 md:w-5 md:h-5 text-amber-600" />
                        <span className="text-amber-800 font-semibold text-sm">Improve</span>
                      </div>
                      <p className="text-amber-700 text-xs md:text-sm">Missing units on answer</p>
                      <p className="text-amber-600 text-xs mt-1">-1 mark</p>
                    </div>

                    <div className="bg-cobalt-50 border border-cobalt-200 rounded-xl md:rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb className="w-4 h-4 md:w-5 md:h-5 text-cobalt-600" />
                        <span className="text-cobalt-800 font-semibold text-sm">Next Step</span>
                      </div>
                      <p className="text-cobalt-700 text-xs md:text-sm">Try a harder forces question</p>
                      <p className="text-cobalt-600 text-xs mt-1">Keep momentum!</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 lg:py-32 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-100 rounded-full text-emerald-700 text-sm font-medium mb-4 md:mb-6">
              <Sparkles className="w-4 h-4" />
              <span>Powerful Features</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 md:mb-6">
              Everything you need to ace your exams
            </h2>
            <p className="text-lg md:text-xl text-slate-600">
              From practice questions to real-time feedback, Absorbit covers every step of your revision journey.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 border border-slate-200 hover:border-slate-300 hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300"
              >
                <div className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-5 md:mb-6 shadow-lg`}>
                  <feature.icon className="w-6 h-6 md:w-7 md:h-7 text-white" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-2 md:mb-3">{feature.title}</h3>
                <p className="text-slate-600 text-sm md:text-base leading-relaxed">{feature.description}</p>
                <ChevronRight className="absolute top-6 right-6 md:top-8 md:right-8 w-5 h-5 text-slate-300 group-hover:text-cobalt-500 group-hover:translate-x-1 transition-all" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subjects Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              All major GCSE & A-Level subjects
            </h2>
            <p className="text-lg text-slate-600">
              Comprehensive coverage across the curriculum
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            {subjects.map((subject, index) => (
              <div
                key={index}
                className="px-5 md:px-6 py-3 md:py-4 bg-white rounded-xl md:rounded-2xl border border-slate-200 hover:border-cobalt-300 hover:shadow-lg hover:shadow-cobalt-100/50 transition-all cursor-default"
              >
                <span className="text-slate-700 font-medium text-sm md:text-base">{subject}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 lg:py-32 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 md:mb-6">
              Simple, effective revision
            </h2>
            <p className="text-lg md:text-xl text-slate-600">
              Three steps to better exam results
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {[
              { step: '1', title: 'Pick a topic', desc: 'Choose from the exact spec points for your exam board.', icon: Target },
              { step: '2', title: 'Show your working', desc: 'Type, draw, or use our whiteboard to solve problems.', icon: PenTool },
              { step: '3', title: 'Get feedback', desc: 'Receive mark-by-mark analysis like a real examiner.', icon: ClipboardCheck }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="relative inline-flex mb-6">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-cobalt-100 to-purple-100 flex items-center justify-center">
                    <item.icon className="w-8 h-8 md:w-10 md:h-10 text-cobalt-600" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-7 h-7 md:w-8 md:h-8 rounded-full bg-cobalt-600 text-white text-sm font-bold flex items-center justify-center">
                    {item.step}
                  </div>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-24 lg:py-32 px-4 sm:px-6 bg-gradient-to-b from-white via-cobalt-50/30 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-100 rounded-full text-amber-700 text-sm font-medium mb-4 md:mb-6">
              <Zap className="w-4 h-4" />
              <span>Incredible Value</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 md:mb-6">
              A tutor for the price of a coffee
            </h2>
            <p className="text-lg md:text-xl text-slate-600">
              Private tutors charge £50+/hour. Absorbit gives you unlimited help across all subjects.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
            {/* Free Plan */}
            <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 border-2 border-slate-200">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Free</h3>
                <p className="text-slate-500 text-sm">Get started with the basics</p>
              </div>
              <div className="mb-6">
                <span className="text-4xl md:text-5xl font-bold text-slate-900">£0</span>
                <span className="text-slate-500 text-sm">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {['5 questions per day', 'Basic AI feedback', '1 subject', 'Progress tracking'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-600 text-sm">
                    <CheckCircle className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={onSignUp}
                className="w-full py-3 md:py-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-colors"
              >
                Start Free
              </button>
            </div>

            {/* Pro Plan */}
            <div className="relative bg-cobalt-600 rounded-2xl md:rounded-3xl p-6 md:p-8 text-white">
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-2">Pro</h3>
                <p className="text-cobalt-100 text-sm">Everything you need to succeed</p>
              </div>
              <div className="mb-6">
                <span className="text-4xl md:text-5xl font-bold">£8.99</span>
                <span className="text-cobalt-100 text-sm">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  'Unlimited questions',
                  'Advanced AI feedback',
                  'All 9 subjects',
                  'Detailed progress analytics',
                  'Whiteboard & notes',
                  'Flashcard generation',
                  'Priority support'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-cobalt-300 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={onSignUp}
                className="w-full py-3 md:py-4 bg-white hover:bg-slate-50 text-cobalt-600 font-semibold rounded-xl transition-colors"
              >
                Get Pro
              </button>
            </div>
          </div>

          {/* Comparison Note */}
          <div className="mt-10 md:mt-16 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-emerald-50 border border-emerald-100 rounded-2xl">
              <div className="text-emerald-600 font-bold text-lg">Save ~98%</div>
              <div className="text-emerald-700 text-sm">compared to private tuition (£500/month avg)</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 lg:py-32 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 md:mb-6">
            Ready to transform your revision?
          </h2>
          <p className="text-lg md:text-xl text-slate-600 mb-8 md:mb-10 max-w-2xl mx-auto">
            Start your revision journey with Absorbit and prepare for your GCSEs and A-Levels with confidence.
          </p>
          <button
            onClick={onSignUp}
            className="px-8 md:px-10 py-4 md:py-5 bg-cobalt-600 hover:bg-cobalt-500 text-white text-lg font-semibold rounded-2xl shadow-xl shadow-cobalt-500/25 hover:shadow-cobalt-500/40 transition-all inline-flex items-center gap-3"
          >
            Get Started Free
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 md:py-16 border-t border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center">
              <img src="/absorbit-logo.png" alt="Absorbit" className="h-20 w-auto" />
            </div>
            <p className="text-sm text-slate-500 text-center md:text-right">
              Designed for focus, built for student confidence.
              <br className="md:hidden" />
              <span className="md:ml-2">© 2025 Absorbit</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
